import { useEffect, useState, createContext, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import TabLink from "../../../components/fragment/tab-link/TabLink";
import DashboardLayoutMain from "../../../layout/dashboard/DashboardLayoutMain";
import {
  approveCustomerKyc,
  getCustomerSingle,
  rejectCustomerKyc,
  suspendCustomer,
  unsuspendCustomer,
} from "../../../redux/customer";
import RootState from "../../../redux/type";
import { ALL_ROUTES } from "../../../routes/AllRoutes";
import "../../../styles/dashboard/customer/CustomersIndex.css";
import DocumentsIndex from "./common/document/DocumentsIndex";
import OverviewComponent from "./common/overview/OverviewComponent";
import Transactions from "./common/transactions/Transactions";
import DocumentModal from "./common/overview/component/document-modal";
import ReuseModal from "../../../components/fragment/modal/ReuseModal";
import SuspensionHistory from "./common/suspnesion/SuspensionHistory";
import { ALL_ICONS } from "../../../assets/icons/AllIcons";
import SARForm from "./common/sarform/SARForm";
import TransactionModal from "../transaction/TransactionModal";
import RazorInputField from "../../../components/razor-reuse/inputField/RazorInputField";
import { toast } from "../../../components/razor-reuse";
import BusinessOverview from "./common/overview/BusinessOverview";

// suspension prop
interface SuspensionProp {
  visible: boolean;
  status: "suspend" | "unsuspend" | "";
  reason: string;
}

interface KycStatusProp {
  visible: boolean;
  status: "approve" | "reject" | "";
  reason: string;
}

interface DocModalProp {
  url: string;
  visible: boolean;
  doc_name: string;
  file_name: string;
  file_sizee: number;
  status: string;
  date: string;
}

// Handle view document modal from parent since modal has to be called at the top
export const OverviewContext = createContext<{
  documentModal: DocModalProp;
  setDocumentModal: React.Dispatch<React.SetStateAction<DocModalProp>>;
  statusModal: SuspensionProp;
  setStatusModal: React.Dispatch<React.SetStateAction<SuspensionProp>>;
  kycStatus: KycStatusProp;
  setKycStatus: React.Dispatch<React.SetStateAction<KycStatusProp>>;
} | null>(null);

const CustomerSingle = () => {
  const navigate = useNavigate();
  const [singleChild, setSingleChild] = useState({});
  const [showModal, setShowModal] = useState(false); // Transaction modal
  const [initialloading, setInitialLoading] = useState(true); /// For the page initial load

  const [documentModal, setDocumentModal] = useState<DocModalProp>({
    visible: false,
    url: "",
    doc_name: "",
    file_name: "",
    file_sizee: 0,
    status: "",
    date: "",
  });
  const [statusModal, setStatusModal] = useState<SuspensionProp>({
    visible: false,
    status: "",
    reason: "",
  });
  //For handling status of user (suspend/unsuspended)
  const [kycStatus, setKycStatus] = useState<KycStatusProp>({
    visible: false,
    status: "",
    reason: "",
  });
  //For handling kyc of user (approve/reject)
  const { loading_modal } = useSelector((state: RootState) => state.customer);
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("Overview");

  // Tab link list
  const tabLinkList = [
    { label: "Overview", active: activeTab === "Overview", onClick: () => {} },
    {
      label: "Transactions",
      active: activeTab === "Transactions",
      onClick: () => {},
    },
    {
      label: "Suspension History",
      active: activeTab === "Suspension History",
      onClick: () => {},
    },
    {
      label: "SAR Form",
      active: activeTab === "SAR Form",
      onClick: () => {},
    },
  ];

  const cust_id = localStorage?.getItem("cors_key") || "";
  const type = searchParams.get("type") || "";

  useEffect(() => {
    if (cust_id) {
      getSingleInfo();
    } else {
      navigate(ALL_ROUTES?.DASHBOARD_CUSTOMER);
    }
  }, []);

  const getSingleInfo = async () => {
    const obj = {
      id: cust_id,
    };
    // Essence of this loading state is to ensure the page is showing a loading state when effect data for the first time \/\/\/\/
    try {
      await dispatch(getCustomerSingle(obj) as any).unwrap();
    } catch (err) {
    } finally {
      setInitialLoading(false); // ⬅️ done loading, even on error
    }
  };

  // Handle the suspension/unsuspension of customers
  const handleSuspension = async () => {
    if (statusModal.reason.length < 1) return;
    const obj = {
      id: cust_id,
      body: { reason: statusModal.reason },
    };
    if (statusModal.status === "suspend") {
      const data = await dispatch(suspendCustomer(obj) as any);
      if (data?.payload?.data?.status) {
        dispatch(
          getCustomerSingle({
            id: cust_id,
          }) as any
        );
      }
    } else {
      const data = await dispatch(unsuspendCustomer(obj) as any);
      if (data?.payload?.data?.status) {
        dispatch(
          getCustomerSingle({
            id: cust_id,
          }) as any
        );
      }
    }

    setStatusModal({
      visible: false,
      status: "",
      reason: "",
    });
  };

  // Handle the approve/reject kyc level 2 of customers
  const handleApproveRejectKyc = async () => {
    if (kycStatus.reason.trim().length < 4 && kycStatus.status === "reject") {
      toast.warning("Message too short!!!");
      return;
    }

    const obj = {
      id: cust_id,
      body: { reason: kycStatus.reason },
    };
    if (kycStatus.status === "approve") {
      const data = await dispatch(approveCustomerKyc(obj) as any);
      if (data?.payload?.data?.status) {
        dispatch(
          getCustomerSingle({
            id: cust_id,
          }) as any
        );
      }
    } else {
      const data = await dispatch(rejectCustomerKyc(obj) as any);
      if (data?.payload?.data?.status) {
        dispatch(
          getCustomerSingle({
            id: cust_id,
          }) as any
        );
      }
    }

    setKycStatus({
      visible: false,
      status: "",
      reason: "",
    });
  };

  return (
    <>
      <OverviewContext.Provider
        value={{
          documentModal,
          setDocumentModal,
          statusModal,
          setStatusModal,
          kycStatus,
          setKycStatus,
        }}
      >
        <DashboardLayoutMain loading={initialloading} title="User" back>
          {/* filter fragment start */}
          {/* <FilterReusableComp hideSearch></FilterReusableComp> */}
          <div className="bg-white grid place-content-end relative">
            <button className="mt-[10px] mr-[20px] stricky top-[1px] text-[12px] flex items-center text-primary border-[1.5px] border-primary py-[4px] px-[8px] rounded-[8px]">
              <figure className="scale-75">{ALL_ICONS.file_export_icon}</figure>
              Export
            </button>
          </div>
          {/* filter fragment end */}
          {/* tab link list start */}
          <TabLink
            onClick={(param) => {
              setActiveTab(param?.label);
            }}
            list={tabLinkList}
            className="single-customer-tab-link"
          />
          {/* tab link list end */}
          {/* smart-send-single- detail wrapper index start */}
          <div className="smart-send-single-customer-detail-wrapper-index">
            {/* overview start */}
            {activeTab === "Overview" && (
              <>
                {type === "business" ? (
                  <BusinessOverview />
                ) : (
                  <OverviewComponent />
                )}
              </>
            )}
            {/* overview end */}
            {/* transaction start */}
            {activeTab === "Transactions" && (
              <>
                <Transactions
                  setShowModal={setShowModal}
                  setSingleChild={setSingleChild}
                />
              </>
            )}
            {/* transaction end */}
            {/* transaction start */}
            {activeTab === "Document History" && (
              <>
                <DocumentsIndex />
              </>
            )}
            {/* transaction end */}
            {/* Suspension history start */}
            {activeTab === "Suspension History" && (
              <>
                <SuspensionHistory />
              </>
            )}
            {/* Suspension history end */}
            {/* sar form start */}
            {activeTab === "SAR Form" && (
              <>
                <SARForm />
              </>
            )}
            {/* sar form end */}
          </div>
          {/* smart-send-single- detail wrapper index end */}
        </DashboardLayoutMain>
        <TransactionModal
          visible={showModal}
          onClose={() => {
            setShowModal((prev) => !prev);
          }}
          details={singleChild}
        />
        <DocumentModal
          visible={documentModal.visible}
          onClose={() => {
            setDocumentModal({
              visible: false,
              url: "",
              doc_name: "",
              file_name: "",
              file_sizee: 0,
              status: "",
              date: "",
            });
          }}
          url={documentModal?.url}
          doc_name={documentModal?.doc_name}
          file_name={documentModal?.file_name}
          file_size={documentModal?.file_sizee || 0}
          status={documentModal?.status}
          date={documentModal?.date}
        />
        {/* Handling Suspending/Unsuspending Customer Modal */}
        <ReuseModal
          visible={statusModal.visible}
          loading={loading_modal}
          onClose={() => {
            setStatusModal({
              visible: false,
              status: "",
              reason: "",
            });
          }}
          title={`${
            statusModal.status === "suspend" ? "Suspend" : "Unsuspend"
          } Customer Account`}
          btnLabel={
            statusModal.status === "suspend"
              ? "Suspend account"
              : "Unsuspend account"
          }
          onBtnClick={handleSuspension}
        >
          <>
            <p className="text-14px] text-gray-01 -mb-[20px]">
              {statusModal.status === "suspend"
                ? "Are you sure you want to suspend this user? This action may restrict their access and impact ongoing activities"
                : "Are you sure you want to unsuspend this user? This will restore their access and allow them to resume activities."}
            </p>

            <RazorInputField
              color="black-light"
              disabled={loading_modal}
              className="!bg-white mt-[10px] [&_textarea]:!bg-white"
              selectClassName={"!bg-red-200"}
              textareaRow={3}
              type="textarea"
              placeholder="Enter reason"
              onChange={(e: any) => {
                const { name, value } = e.target;
                setStatusModal((prev) => ({ ...prev, [name]: value }));
              }}
              name="reason"
              value={statusModal.reason}
              capitalize
            />
          </>
        </ReuseModal>

        {/* Approve/reject Level 2 KYC Modal */}
        <ReuseModal
          visible={kycStatus.visible}
          onBtnClick={handleApproveRejectKyc}
          loading={loading_modal}
          title={`Proceed to ${
            kycStatus?.status === "approve" ? "Approve" : "Reject"
          } this User’s Details`}
          btnLabel={kycStatus?.status === "approve" ? "Approve" : "Reject"}
          onClose={() => {
            setKycStatus({
              visible: false,
              status: "",
              reason: "",
            });
          }}
        >
          <>
            <p className="">
              {kycStatus.status === "approve"
                ? "Carefully review the submitted KYC document. You can approve the document if all details are correct."
                : "Provide a reason for rejecting this document to help the user understand what needs to be corrected."}
            </p>
            <RazorInputField
              color="black-light"
              className="!bg-white mt-[10px] [&_textarea]:!bg-white"
              selectClassName={"!bg-red-200"}
              textareaRow={3}
              type="textarea"
              placeholder={
                kycStatus.status === "reject"
                  ? "Enter Rejection Reason"
                  : "Enter Comment (Optional)"
              }
              disabled={loading_modal}
              onChange={(e: any) => {
                const { name, value } = e.target;
                setKycStatus((prev) => ({ ...prev, [name]: value }));
              }}
              name="reason"
              value={kycStatus.reason}
              capitalize
            />
          </>
        </ReuseModal>
        {/* Approve/reject Level 2 KYC */}
      </OverviewContext.Provider>
    </>
  );
};

export default CustomerSingle;

export const useOverviewContext = () => {
  const context = useContext(OverviewContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};
